// TCEBP - Tree Cutting and/or Earth Balling Permit for National Government Agencies
//
// ISSUANCE OF TREE CUTTING AND/OR EARTH BALLING PERMIT FOR TREES AFFECTED BY PROJECTS OF NATIONAL GOVERNMENT AGENCIES (DPWH, DOTR, DepEd, DA, DOH, CHED, DOE, and NIA)
// This Permit serves as proof of authorization for the removal/cutting and/or relocation of trees affected by projects of the National Government Agencies (DPWH, DOTR, DepEd, DA, DOH, CHED, DOE and NIA)

import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Modal from '@/components/ui/modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import '@/components/ui/styles/customScrollBar.css';
import { Textarea } from '@/components/ui/textarea';
import { CheckboxItem, UploadCard } from '@/pages/user/ApplicationForms/CSAWForm/CSAWFormUtils';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Loader2 } from "lucide-react";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import FormStepIndicator from '../FormStepIndicator';

const CREATE_TCEBP_PERMIT = gql`
   mutation CreateTCEBPPermit($input: TCEBPPermitInput!) {
      createTCEBPPermit(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         name
         projectLocation
         officeName
         officeAddress
         contactNumber
         requestType
         purpose
         files {
         letterOfIntent { filename contentType }
         applicationLetter { filename contentType }
         lguEndorsement { filename contentType }
         landTenurial { filename contentType }
         siteDevelopmentPlan { filename contentType }
         environmentalCompliance { filename contentType }
         fpic { filename contentType }
         ownerConsent { filename contentType }
         pambClearance { filename contentType }
         }
      }
   }
`;

const SAVE_TCEBP_PERMIT_DRAFT = gql`
   mutation SaveTCEBPPermitDraft($input: TCEBPPermitInput!) {
      saveTCEBPPermitDraft(input: $input) {
         id
         applicationNumber
         status
         name
         projectLocation
         officeName
         address
         contactNumber
         requestType
         purpose
         files {
         letterOfIntent { filename contentType }
         lguEndorsement { filename contentType }
         landTenurial { filename contentType }
         siteDevelopmentPlan { filename contentType }
         environmentalCompliance { filename contentType }
         fpic { filename contentType }
         ownerConsent { filename contentType }
         pambClearance { filename contentType }
         }
      }
   }
`;

const VERIFY_INVENTORY_REQUEST = gql`
   query VerifyInventoryRequest($applicationNumber: String!, $requestType: String!) {
      verifyInventoryRequest(applicationNumber: $applicationNumber, requestType: $requestType) {
         id
         applicationNumber
         requestType
         ApprovedInventoryRequest
         ... on TCEBPPermit {
            name
            projectLocation
            officeName
            officeAddress
            contactNumber
            purpose
         }
      }
   }
`;

// Agency options for the dropdown
const AGENCY_OPTIONS = [
   { value: 'DPWH', label: 'DPWH - Department of Public Works and Highways' },
   { value: 'DOTR', label: 'DOTR - Department of Transportation' },
   { value: 'DEPED', label: 'DEPED - Department of Education' },
   { value: 'DA', label: 'DA - Department of Agriculture' },
   { value: 'DOH', label: 'DOH - Department of Health' },
   { value: 'CHED', label: 'CHED - Commission on Higher Education' },
   { value: 'DOE', label: 'DOE - Department of Energy' },
   { value: 'NIA', label: 'NIA - National Irrigation Administration' }
];

const TCEBPForm = () => {
   const navigate = useNavigate();
   const [currentStep, setCurrentStep] = useState(() => {
      const savedStep = localStorage.getItem('tcebpFormStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
   });
   const [formData, setFormData] = useState(() => {
      const savedFormData = localStorage.getItem('tcebpFormData');
      return savedFormData ? JSON.parse(savedFormData) : {
         applicationType: 'Tree Cutting and/or Earth Balling Permit',
         requestType: '',
         inventoryPurpose: '',
         name: '',
         projectLocation: '',
         officeName: '',
         address: '',
         contactNumber: '',
         purpose: '',
         isRequestForCutting: false,
         isRequestForEarthBalling: false,
         files: {
            letterOfIntent: [],
            applicationLetter: [],
            lguEndorsement: [],
            landTenurial: [],
            siteDevelopmentPlan: [],
            environmentalCompliance: [],
            fpic: [],
            ownerConsent: [],
            pambClearance: []
         },
         dateOfSubmission: '',
         status: '',
         isWithinTimberland: false,
         isTitledProperty: false,
         isWithinProtectedArea: false,
         documentRequirements: {
            isWithinTimberland: false,
            isTitledProperty: false,
            isWithinProtectedArea: false
         }
      };
   });
   const [modalOpen, setModalOpen] = useState(false);
   const [modalContent, setModalContent] = useState({ title: '', message: '' });
   const [createTCEBPPermit] = useMutation(CREATE_TCEBP_PERMIT);
   const [saveTCEBPPermitDraft] = useMutation(SAVE_TCEBP_PERMIT_DRAFT);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSavingDraft, setIsSavingDraft] = useState(false);
   const [showSubmitDialog, setShowSubmitDialog] = useState(false);
   const [showDraftDialog, setShowDraftDialog] = useState(false);
   const [previousApplicationNumber, setPreviousApplicationNumber] = useState('');
   const [verifiedInventoryData, setVerifiedInventoryData] = useState(null);
   const [isVerifyingRequest, setIsVerifyingRequest] = useState(false);

   const [verifyInventoryRequest] = useLazyQuery(VERIFY_INVENTORY_REQUEST, {
      onCompleted: (data) => {
         if (data?.verifyInventoryRequest) {
            setVerifiedInventoryData(data.verifyInventoryRequest);
            toast.success('Inventory request verified successfully', {
               description: 'You can now proceed with your application'
            });
            // Pre-fill form data from inventory request
            setFormData(prev => ({
               ...prev,
               name: data.verifyInventoryRequest.name,
               projectLocation: data.verifyInventoryRequest.projectLocation,
               officeName: data.verifyInventoryRequest.officeName,
               address: data.verifyInventoryRequest.officeAddress,
               contactNumber: data.verifyInventoryRequest.contactNumber,
               purpose: data.verifyInventoryRequest.purpose,
               // Set request type based on the current form selection
               isRequestForCutting: formData.requestType === 'Cutting',
               isRequestForEarthBalling: formData.requestType === 'Earth Balling'
            }));
         }
         setIsVerifyingRequest(false);
      },
      onError: (error) => {
         toast.error(error.message || 'Failed to verify inventory request');
         setIsVerifyingRequest(false);
         setPreviousApplicationNumber('');
      }
   });

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      if (name === 'requestType') {
         setFormData(prev => ({
            ...prev,
            requestType: value,
            inventoryPurpose: value !== 'Inventory' ? '' : prev.inventoryPurpose,
            isRequestForCutting: value === 'Cutting',
            isRequestForEarthBalling: value === 'Earth Balling'
         }));
      } else if (name === 'inventoryPurpose') {
         setFormData(prev => ({
            ...prev,
            inventoryPurpose: value,
            isRequestForCutting: value === 'forCutting',
            isRequestForEarthBalling: value === 'forEarthBalling'
         }));
      } else if (name.includes('.')) {
         const [parent, child] = name.split('.');
         setFormData(prev => ({
            ...prev,
            [parent]: {
               ...prev[parent],
               [child]: value
            }
         }));
      } else {
         setFormData(prev => ({ ...prev, [name]: value }));
      }
   };

   const handleFileChange = (e, documentType) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFormData(prev => ({
            ...prev,
            files: {
               ...prev.files,
               [documentType]: [...prev.files[documentType], ...newFiles]
            }
         }));
      }
   };

   const removeFile = (documentType, fileToRemove) => {
      setFormData(prev => ({
         ...prev,
         files: {
            ...prev.files,
            [documentType]: prev.files[documentType].filter(file => file !== fileToRemove)
         }
      }));
   };

   const handleNextStep = () => {
      if (currentStep === 0) {
         if (!formData.requestType) {
            toast.error("Please select a request type");
            return;
         }
         if (formData.requestType === 'Inventory' && !formData.inventoryPurpose) {
            toast.error("Please select what this inventory request is for");
            return;
         }
         const verificationEnabled = localStorage.getItem('verificationEnabled') !== 'false';
         if (verificationEnabled && (formData.requestType === 'Cutting' || formData.requestType === 'Earth Balling') && !verifiedInventoryData) {
            toast.error("Please verify your previous inventory request first");
            return;
         }
      }

      // Skip Document Requirements step for Inventory requests
      if (currentStep === 1 && formData.requestType === 'Inventory') {
         setCurrentStep(3); // Skip to Documents step
         return;
      }

      // Skip Document Requirements step when moving from Personal Information to Documents for Inventory
      if (currentStep === 2 && formData.requestType === 'Inventory') {
         setCurrentStep(4); // Skip to Review step
      } else {
         setCurrentStep(prev => prev + 1);
      }
   };

   const handlePrevStep = () => {
      // Handle going back from Documents step for Inventory requests
      if (currentStep === 3 && formData.requestType === 'Inventory') {
         setCurrentStep(1); // Go back to Personal Information
         return;
      }

      // Handle going back from Review step for Inventory requests
      if (currentStep === 4 && formData.requestType === 'Inventory') {
         setCurrentStep(3); // Go back to Documents
         return;
      }

      setCurrentStep(prev => prev - 1);
   };

   const handleSaveAsDraft = () => {
      setShowDraftDialog(true);
   };

   const handleConfirmSaveAsDraft = async () => {
      try {
         setIsSavingDraft(true);
         const input = {
            name: formData.name,
            projectLocation: formData.projectLocation,
            officeName: formData.officeName,
            address: formData.address,
            contactNumber: formData.contactNumber,
            requestType: formData.requestType,
            purpose: formData.purpose,
            isWithinTimberland: Boolean(formData.isWithinTimberland),
            isTitledProperty: Boolean(formData.isTitledProperty),
            isWithinProtectedArea: Boolean(formData.isWithinProtectedArea),
            isRequestForCutting: Boolean(formData.isRequestForCutting),
            isRequestForEarthBalling: Boolean(formData.isRequestForEarthBalling),
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await saveTCEBPPermitDraft({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.saveTCEBPPermitDraft) {
            setModalContent({
               title: 'Draft saved successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);
            localStorage.removeItem('tcebpFormStep');
            localStorage.removeItem('tcebpFormData');
         }
      } catch (error) {
         console.error('Error saving draft:', error);
         toast.error("Error saving draft: " + error.message);
      } finally {
         setIsSavingDraft(false);
         setShowDraftDialog(false);
      }
   };

   const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      setShowSubmitDialog(true);
   };

   const handleConfirmSubmit = async () => {
      try {
         setIsSubmitting(true);
         const input = {
            name: formData.name,
            projectLocation: formData.projectLocation,
            officeName: formData.officeName,
            officeAddress: formData.address,
            contactNumber: formData.contactNumber,
            requestType: formData.requestType,
            purpose: formData.purpose,
            isWithinTimberland: Boolean(formData.isWithinTimberland),
            isTitledProperty: Boolean(formData.isTitledProperty),
            isWithinProtectedArea: Boolean(formData.isWithinProtectedArea),
            isRequestForCutting: Boolean(formData.isRequestForCutting),
            isRequestForEarthBalling: Boolean(formData.isRequestForEarthBalling),
            files: {},
            verifiedInventoryId: (formData.requestType === 'Cutting' || formData.requestType === 'Earth Balling') && verifiedInventoryData
               ? verifiedInventoryData.id
               : undefined
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         // Validate required fields before submission
         if (!input.requestType) {
            throw new Error('Request Type is required');
         }

         const token = localStorage.getItem('token');
         const { data } = await createTCEBPPermit({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.createTCEBPPermit) {
            setModalContent({
               title: 'Application submitted successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);
            localStorage.removeItem('tcebpFormStep');
            localStorage.removeItem('tcebpFormData');
         }
      } catch (error) {
         console.error('Error submitting application:', error);
         toast.error("Error submitting application: " + error.message);
      } finally {
         setIsSubmitting(false);
         setShowSubmitDialog(false);
      }
   };

   // Helper function to read file as base64
   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result.split(',')[1]);
         reader.onerror = error => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const handleCheckboxChange = (e) => {
      const { name, checked } = e.target;

      // Map of checkbox names to their corresponding file types
      const fileTypeMap = {
         isWithinTimberland: 'landTenurial',
         isTitledProperty: 'ownerConsent',
         isWithinProtectedArea: 'pambClearance'
      };

      // Clear associated files if requirement is unchecked
      if (!checked && fileTypeMap[name]) {
         setFormData(prev => ({
            ...prev,
            [name]: checked,
            documentRequirements: {
               ...prev.documentRequirements,
               [name]: checked
            },
            files: {
               ...prev.files,
               [fileTypeMap[name]]: [] // Clear the files for this requirement
            }
         }));
      } else {
         setFormData(prev => ({
            ...prev,
            [name]: checked,
            documentRequirements: {
               ...prev.documentRequirements,
               [name]: checked
            }
         }));
      }
   };

   const steps = [
      { title: "Select Request Type", description: "Choose the type of request" },
      { title: "Applicant Details", description: "Fill in your personal information" },
      { title: "Document Requirements", description: "Specify document requirements" },
      { title: "Upload Documents", description: "Upload necessary documents" },
      { title: "Review Your Application", description: "Review and submit your application" },
   ];

   useEffect(() => {
      localStorage.setItem('tcebpFormStep', currentStep.toString());
   }, [currentStep]);

   useEffect(() => {
      localStorage.setItem('tcebpFormData', JSON.stringify(formData));
   }, [formData]);

   useEffect(() => {
      const handleBeforeUnload = (e) => {
         // If it's a refresh, persist the data
         if (e.persisted) {
            return;
         }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
         window.removeEventListener('beforeunload', handleBeforeUnload);

         // Get the navigation type
         const perfEntries = performance.getEntriesByType('navigation');
         const navType = perfEntries.length > 0 ? perfEntries[0].type : '';

         // Only clear if it's not a refresh/reload
         if (navType !== 'reload') {
            // Clear form data from localStorage when component unmounts (not on refresh)
            localStorage.removeItem('tcebpFormStep');
            localStorage.removeItem('tcebpFormData');
            // Reset form data to initial state
            setFormData({
               applicationType: 'Tree Cutting and/or Earth Balling Permit',
               requestType: '',
               inventoryPurpose: '',
               name: '',
               projectLocation: '',
               officeName: '',
               address: '',
               contactNumber: '',
               purpose: '',
               isRequestForCutting: false,
               isRequestForEarthBalling: false,
               files: {
                  letterOfIntent: [],
                  applicationLetter: [],
                  lguEndorsement: [],
                  landTenurial: [],
                  siteDevelopmentPlan: [],
                  environmentalCompliance: [],
                  fpic: [],
                  ownerConsent: [],
                  pambClearance: []
               },
               dateOfSubmission: '',
               status: '',
               isWithinTimberland: false,
               isTitledProperty: false,
               isWithinProtectedArea: false,
               documentRequirements: {
                  isWithinTimberland: false,
                  isTitledProperty: false,
                  isWithinProtectedArea: false
               }
            });
            // Reset current step
            setCurrentStep(0);
            // Reset verification data
            setVerifiedInventoryData(null);
            setPreviousApplicationNumber('');
         }
      };
   }, []);

   const validateStep = (step) => {
      switch (step) {
         case 0:
            if (!formData.requestType) return false;
            if (formData.requestType === 'Inventory' && !formData.inventoryPurpose) return false;
            return true;
         case 1:
            return !!formData.name && !!formData.address && !!formData.contactNumber; // double !! to check if the value is not null or undefined
         case 2:
            if (formData.requestType === 'Inventory') return true;
            return true;
         case 3:
            return true;
         default:
            return true;
      }
   };

   const renderPersonalInfoStep = () => {
      return (
         <div className="space-y-4">
            <div>
               <Label htmlFor="name">Name</Label>
               <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
               />
            </div>

            <div>
               <Label htmlFor="projectLocation">Project Location (Barangay, Municipality, Province)</Label>
               <Input
                  id="projectLocation"
                  name="projectLocation"
                  value={formData.projectLocation}
                  onChange={handleInputChange}
                  placeholder="Enter project location"
               />
            </div>

            <div className="space-y-2">
               <Label>Agency</Label>
               <div>
                  <Label htmlFor="officeName">Office Name</Label>
                  <select
                     id="officeName"
                     name="officeName"
                     value={formData.officeName}
                     onChange={handleInputChange}
                     className="w-full p-2 border rounded-md"
                  >
                     <option value="">Select Office</option>
                     {AGENCY_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                           {option.label}
                        </option>
                     ))}
                  </select>
               </div>
            </div>

            <div>
               <Label htmlFor="address">Office Address</Label>
               <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter office address"
               />
            </div>

            <div>
               <Label htmlFor="contactNumber">Contact Number</Label>
               <Input
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  placeholder="Enter contact number"
               />
            </div>

            <div>
               <Label htmlFor="purpose">Purpose</Label>
               <Textarea
                  id="purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  placeholder="Enter purpose"
               />
            </div>
         </div>
      );
   };

   const handleVerifyInventoryRequest = async () => {
      if (!previousApplicationNumber) {
         toast.error("Please enter an application number");
         return;
      }

      // Proper regex pattern for TCEBP application numbers
      const permitTypeMatch = previousApplicationNumber.match(/^PMDQ-TCEBP-\d{4}-\d{4}-\d{6}$/);
      if (!permitTypeMatch) {
         toast.error("Invalid TCEBP application number format");
         return;
      }

      setIsVerifyingRequest(true);
      try {
         const { data } = await verifyInventoryRequest({
            variables: {
               applicationNumber: previousApplicationNumber,
               requestType: formData.requestType
            }
         });

         if (data?.verifyInventoryRequest) {
            const verifiedData = data.verifyInventoryRequest;
            setVerifiedInventoryData(verifiedData);
            setFormData(prev => ({
               ...prev,
               name: verifiedData.name,
               projectLocation: verifiedData.projectLocation,
               officeName: verifiedData.officeName,
               address: verifiedData.officeAddress,
               contactNumber: verifiedData.contactNumber,
               purpose: verifiedData.purpose,
               isRequestForCutting: formData.requestType === 'Cutting',
               isRequestForEarthBalling: formData.requestType === 'Earth Balling'
            }));
         }
      } catch (error) {
         console.error('Error verifying inventory request:', error);
         toast.error(error.message || 'Failed to verify inventory request');
         setPreviousApplicationNumber('');
      } finally {
         setIsVerifyingRequest(false);
      }
   };

   const formatLabel = (key) => {
      // Special case for inventory purpose values
      if (key === 'inventoryPurpose') {
         return 'Inventory Purpose';
      }

      // Convert camelCase to Title Case with spaces
      return key
         .replace(/([A-Z])/g, ' $1') // Add space before capital letters
         .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
         .trim(); // Remove any extra spaces
   };

   const formatReviewValue = (key, value) => {
      if (typeof value === 'boolean') {
         return value ? 'Yes' : 'No';
      }
      if (key === 'inventoryPurpose') {
         // Format inventory purpose values
         if (value === 'forCutting') return 'For Cutting';
         if (value === 'forEarthBalling') return 'For Earth Balling';
      }
      return value || 'Not specified';
   };

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col justify-between pt-[83px]">
         <div className="container mx-auto px-4 flex-grow">
            <h1 className="text-3xl font-[700] text-green-800 dark:text-green-500 mb-6 text-center">
               Tree Cutting and/or Earth Balling Permit Application
            </h1>
            <div className="max-w-2xl mx-auto mb-6">
               <FormStepIndicator currentStep={currentStep + 1} formType="TCEBP" />
            </div>
            <Card className="max-w-2xl mx-auto shadow-lg bg-background">
               <CardHeader>
                  <CardTitle className="text-foreground">{steps[currentStep].title}</CardTitle>
               </CardHeader>
               <CardContent>
                  <form onSubmit={handleSubmit}>
                     {currentStep === 0 && (
                        <div className="space-y-4">
                           <RadioGroup
                              name="requestType"
                              value={formData.requestType}
                              onValueChange={(value) => handleInputChange({ target: { name: 'requestType', value } })}
                              className="space-y-4"
                           >
                              <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
                                 <RadioGroupItem value="Cutting" id="Cutting" />
                                 <Label htmlFor="Cutting" className="flex-grow text-foreground">
                                    Request for Tree Cutting (NGA's)
                                 </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
                                 <RadioGroupItem value="Earth Balling" id="EarthBalling" />
                                 <Label htmlFor="EarthBalling" className="flex-grow text-foreground">
                                    Request for Earth Balling (NGA's)
                                 </Label>
                              </div>
                              <div className="flex flex-col space-y-2">
                                 <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
                                    <RadioGroupItem value="Inventory" id="Inventory" />
                                    <Label htmlFor="Inventory" className="flex-grow text-foreground">
                                       Request for Tree Inventory (NGA'S)
                                    </Label>
                                 </div>
                                 {formData.requestType === 'Inventory' && (
                                    <div className="ml-6 space-y-2 p-4 border-l-2 border-input">
                                       <Label className="text-sm font-medium mb-2">What is this inventory request for?</Label>
                                       <RadioGroup
                                          name="inventoryPurpose"
                                          value={formData.inventoryPurpose}
                                          onValueChange={(value) => handleInputChange({ target: { name: 'inventoryPurpose', value } })}
                                          className="space-y-2"
                                       >
                                          <div className="flex items-center space-x-2">
                                             <RadioGroupItem value="forCutting" id="forCutting" />
                                             <Label htmlFor="forCutting">For Tree Cutting</Label>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                             <RadioGroupItem value="forEarthBalling" id="forEarthBalling" />
                                             <Label htmlFor="forEarthBalling">For Earth Balling</Label>
                                          </div>
                                       </RadioGroup>
                                    </div>
                                 )}
                              </div>
                           </RadioGroup>

                           {(formData.requestType === 'Cutting' || formData.requestType === 'Earth Balling') && (
                              <div className="space-y-2">
                                 <Label>Previous Inventory Application Number</Label>
                                 <div className="flex gap-2">
                                    <Input
                                       type="text"
                                       value={previousApplicationNumber}
                                       onChange={(e) => setPreviousApplicationNumber(e.target.value)}
                                       placeholder="Enter inventory application number"
                                       disabled={isVerifyingRequest || verifiedInventoryData}
                                    />
                                    <Button
                                       type="button"
                                       variant="outline"
                                       className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                                       onClick={handleVerifyInventoryRequest}
                                       disabled={isVerifyingRequest || verifiedInventoryData || !previousApplicationNumber.trim()}
                                    >
                                       {isVerifyingRequest ? (
                                          <>
                                             <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                             Verifying
                                          </>
                                       ) : (
                                          'Verify Application'
                                       )}
                                    </Button>
                                 </div>
                                 {verifiedInventoryData && (
                                    <div className="text-sm text-green-600 mt-2">
                                       ✓ Verified inventory request
                                    </div>
                                 )}
                              </div>
                           )}
                        </div>
                     )}

                     {currentStep === 1 && renderPersonalInfoStep()}

                     {currentStep === 2 && formData.requestType !== 'Inventory' && (
                        <div className="space-y-4">
                           <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">
                              Please check the boxes that apply to you. In the next step, you will be required to upload the corresponding documents.
                           </p>
                           <div className="space-y-2">
                              <CheckboxItem
                                 id="isWithinTimberland"
                                 label="Is the property within timberland?"
                                 checked={formData.isWithinTimberland}
                                 onChange={handleCheckboxChange}
                              />
                              <CheckboxItem
                                 id="isTitledProperty"
                                 label="Is it a titled property?"
                                 checked={formData.isTitledProperty}
                                 onChange={handleCheckboxChange}
                              />
                              <CheckboxItem
                                 id="isWithinProtectedArea"
                                 label="Is it within Protected Area?"
                                 checked={formData.isWithinProtectedArea}
                                 onChange={handleCheckboxChange}
                              />
                           </div>
                        </div>
                     )}

                     {currentStep === 3 && (
                        <div className="space-y-4">
                           <div className="h-[500px] overflow-y-auto pr-4 custom-scrollbar">
                              {formData.requestType === 'Inventory' ? (
                                 <UploadCard
                                    label="Letter of Intent"
                                    documentLabel="Upload Letter of Intent (1 original)"
                                    files={formData.files.letterOfIntent}
                                    onFileChange={(e) => handleFileChange(e, 'letterOfIntent')}
                                    onRemoveFile={(file) => removeFile('letterOfIntent', file)}
                                 />
                              ) : (
                                 <>
                                    <UploadCard
                                       label="Application Letter"
                                       documentLabel="Upload Application Letter (1 original)"
                                       files={formData.files.applicationLetter}
                                       onFileChange={(e) => handleFileChange(e, 'applicationLetter')}
                                       onRemoveFile={(file) => removeFile('applicationLetter', file)}
                                    />
                                    <UploadCard
                                       label="LGU Endorsement"
                                       documentLabel="Upload LGU Endorsement/Certification of No Objection from Sanguninian (Barangay or Municipal) (1 original)"
                                       files={formData.files.lguEndorsement}
                                       onFileChange={(e) => handleFileChange(e, 'lguEndorsement')}
                                       onRemoveFile={(file) => removeFile('lguEndorsement', file)}
                                    />
                                    {formData.isWithinTimberland && (
                                       <UploadCard
                                          label="Land Tenurial Instrument"
                                          documentLabel="Upload Approved Land Tenurial Instrument (LTI)/Special Land Use Permit (SLUP), if within timberland (1 photocopy)"
                                          files={formData.files.landTenurial}
                                          onFileChange={(e) => handleFileChange(e, 'landTenurial')}
                                          onRemoveFile={(file) => removeFile('landTenurial', file)}
                                       />
                                    )}
                                    <UploadCard
                                       label="Site Development Plan"
                                       documentLabel="Upload Approved Site Development Plan/Infrastructure Plan with tree charting (1 original)"
                                       files={formData.files.siteDevelopmentPlan}
                                       onFileChange={(e) => handleFileChange(e, 'siteDevelopmentPlan')}
                                       onRemoveFile={(file) => removeFile('siteDevelopmentPlan', file)}
                                    />
                                    <UploadCard
                                       label="Environmental Compliance"
                                       documentLabel="Upload Certificate of Non-Coverage or Environmental Compliance Certificate (CNC/ECC) (1 photocopy)"
                                       files={formData.files.environmentalCompliance}
                                       onFileChange={(e) => handleFileChange(e, 'environmentalCompliance')}
                                       onRemoveFile={(file) => removeFile('environmentalCompliance', file)}
                                    />
                                    <UploadCard
                                       label="FPIC"
                                       documentLabel="Upload Free, Prior and Informed Consent (FPIC) (if applicable)"
                                       files={formData.files.fpic}
                                       onFileChange={(e) => handleFileChange(e, 'fpic')}
                                       onRemoveFile={(file) => removeFile('fpic', file)}
                                    />
                                    {formData.isTitledProperty && (
                                       <UploadCard
                                          label="Owner's Consent"
                                          documentLabel="Upload Waiver/consent of owner's (if titled property) (1 original)"
                                          files={formData.files.ownerConsent}
                                          onFileChange={(e) => handleFileChange(e, 'ownerConsent')}
                                          onRemoveFile={(file) => removeFile('ownerConsent', file)}
                                       />
                                    )}
                                    {formData.isWithinProtectedArea && (
                                       <UploadCard
                                          label="PAMB Clearance"
                                          documentLabel="Upload PAMB Clearance/Resolution (if within Protected Area) (1 original)"
                                          files={formData.files.pambClearance}
                                          onFileChange={(e) => handleFileChange(e, 'pambClearance')}
                                          onRemoveFile={(file) => removeFile('pambClearance', file)}
                                       />
                                    )}
                                 </>
                              )}
                           </div>
                        </div>
                     )}

                     {currentStep === 4 && (
                        <div className="space-y-6 h-[500px] overflow-y-auto custom-scrollbar">
                           <div className="review-step-container custom-scrollbar flex-grow overflow-auto">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 {(formData.requestType === 'Cutting' || formData.requestType === 'Earth Balling') && verifiedInventoryData && (
                                    <div className="col-span-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                       <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">
                                          Verified Inventory Request
                                       </h4>
                                       <p className="text-sm text-green-600 dark:text-green-300">
                                          Application Number: {previousApplicationNumber}
                                       </p>
                                    </div>
                                 )}

                                 {Object.entries(formData)
                                    .filter(([key]) =>
                                       key !== 'status' &&
                                       key !== 'dateOfSubmission' &&
                                       key !== 'files' &&
                                       key !== 'documentRequirements' &&
                                       key !== 'isRequestForCutting' &&
                                       key !== 'isRequestForEarthBalling'
                                    )
                                    .map(([key, value]) => (
                                       <div key={key} className="bg-background border rounded-lg p-3">
                                          <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                             {formatLabel(key)}
                                          </h4>
                                          <p className="text-foreground text-sm">
                                             {formatReviewValue(key, value)}
                                          </p>
                                       </div>
                                    ))}
                              </div>

                              {formData.requestType !== 'Inventory' && formData.documentRequirements && (
                                 <div className="bg-background border rounded-lg p-3 mt-4">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">
                                       Document Requirements
                                    </h4>
                                    <div className="space-y-2">
                                       {Object.entries(formData.documentRequirements).map(([key, value]) => (
                                          <div key={key} className="flex items-center">
                                             <span className="text-foreground text-sm">
                                                {formatLabel(key)}: {value ? 'Yes' : 'No'}
                                             </span>
                                          </div>
                                       ))}
                                    </div>
                                 </div>
                              )}

                              <div className="bg-background border rounded-lg p-3 mt-4">
                                 <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">
                                    Uploaded Files
                                 </h4>
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.entries(formData.files).map(([docType, files]) => (
                                       <div key={docType} className="border-b dark:border-gray-700 pb-2">
                                          <h5 className="font-medium text-foreground mb-1 text-xs">
                                             {formatLabel(docType)}
                                          </h5>
                                          {files.length > 0 ? (
                                             <ul className="list-disc list-inside">
                                                {files.map((file, index) => (
                                                   <li key={index} className="text-xs text-muted-foreground">
                                                      {file.name}
                                                   </li>
                                                ))}
                                             </ul>
                                          ) : (
                                             <p className="text-xs text-muted-foreground">No files uploaded</p>
                                          )}
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           </div>
                        </div>
                     )}
                  </form>
               </CardContent>
               <CardFooter className="mt-14 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="w-full sm:w-auto">
                     {currentStep > 0 && (
                        <Button
                           type="button"
                           variant="outline"
                           onClick={handlePrevStep}
                           className="w-full sm:w-auto"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Previous
                        </Button>
                     )}
                  </div>
                  <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
                     {currentStep < steps.length - 1 ? (
                        <Button
                           type="button"
                           onClick={handleNextStep}
                           className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Next
                        </Button>
                     ) : (
                        <>
                           <Button
                              type="button"
                              variant="outline"
                              onClick={handleSaveAsDraft}
                              className="w-full sm:w-auto"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSavingDraft ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                 </>
                              ) : (
                                 'Save as Draft'
                              )}
                           </Button>
                           <Button
                              type="submit"
                              onClick={handleSubmit}
                              className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSubmitting ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                 </>
                              ) : (
                                 'Submit Application'
                              )}
                           </Button>
                        </>
                     )}
                  </div>
               </CardFooter>
            </Card>
         </div>
         <Modal
            isOpen={modalOpen}
            title={modalContent.title}
            message={modalContent.message}
            onClose={() => setModalOpen(false)}
            onHome={() => navigate('/home')}
            onApplications={() => navigate('/applicationsStatus')}
         />
         <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please review all details carefully before proceeding.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSubmit}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSubmitting ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Submitting...
                        </>
                     ) : (
                        'Submit'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Save as Draft</AlertDialogTitle>
                  <AlertDialogDescription>
                     Do you want to save your current progress as a draft? You can continue editing it later.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSaveAsDraft}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSavingDraft ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Saving...
                        </>
                     ) : (
                        'Save Draft'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
};

export default TCEBPForm;
